import { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Plus, Trash2, X, Pencil } from 'lucide-react';
import toast from 'react-hot-toast';

export default function Debits() {
  const [debits, setDebits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [drawerMode, setDrawerMode] = useState('add');
  const [selectedDebit, setSelectedDebit] = useState(null);
  const [options, setOptions] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'debit_date', direction: 'desc' });
  const [filters, setFilters] = useState({
    start_date: '',
    end_date: '',
    debit_type: '',
    debit_from: '',
  });
  const { isAdmin } = useAuth();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async (overrideFilters) => {
    try {
      const activeFilters = overrideFilters || filters;
      const debitParams = {};
      if (activeFilters.start_date) debitParams.start_date = activeFilters.start_date;
      if (activeFilters.end_date) debitParams.end_date = activeFilters.end_date;
      if (activeFilters.debit_type) debitParams.debit_type = activeFilters.debit_type;
      if (activeFilters.debit_from) debitParams.debit_from = activeFilters.debit_from;

      const [debitsRes, optionsRes] = await Promise.all([
        api.get('/debit', { params: debitParams }),
        api.get('/debit/options'),
      ]);
      setDebits(debitsRes.data.data || []);
      setOptions(optionsRes.data.data || {});
    } catch (error) {
      toast.error('Failed to load debits');
    } finally {
      setLoading(false);
    }
  };

  const filteredDebits = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let rows = Array.isArray(debits) ? [...debits] : [];
    if (q) {
      rows = rows.filter((d) => {
        const haystack = [
          d?.beneficiary,
          d?.debit_type,
          d?.debit_from,
          d?.cashier,
          d?.comments,
          d?.debit_date,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(q);
      });
    }

    const { key, direction } = sortConfig;
    const dir = direction === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      const av = a?.[key];
      const bv = b?.[key];

      if (['inr', 'aed', 'sent_money', 'return_amount'].includes(key)) {
        const an = Number(av ?? 0);
        const bn = Number(bv ?? 0);
        return (an - bn) * dir;
      }

      if (key === 'debit_date') {
        const ad = av ? new Date(av).getTime() : 0;
        const bd = bv ? new Date(bv).getTime() : 0;
        return (ad - bd) * dir;
      }

      const as = String(av ?? '').toLowerCase();
      const bs = String(bv ?? '').toLowerCase();
      if (as < bs) return -1 * dir;
      if (as > bs) return 1 * dir;
      return 0;
    });
    return rows;
  }, [debits, searchQuery, sortConfig]);

  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm();

  const onSubmit = async (data) => {
    try {
      if (drawerMode === 'edit' && selectedDebit?.id) {
        await api.put(`/debit/${selectedDebit.id}`, data);
        toast.success('Debit updated successfully');
      } else {
        await api.post('/debit', data);
        toast.success('Debit added successfully');
      }
      setShowModal(false);
      setDrawerMode('add');
      setSelectedDebit(null);
      reset();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || (drawerMode === 'edit' ? 'Failed to update debit' : 'Failed to add debit'));
    }
  };

  const openAddDrawer = () => {
    setDrawerMode('add');
    setSelectedDebit(null);
    reset({
      debit_date: '',
      beneficiary: '',
      debit_type: '',
      debit_from: '',
      inr: '',
      aed: '',
      return_amount: '',
      cashier: '',
      comments: '',
    });
    setShowModal(true);
  };

  const openEditDrawer = (debit) => {
    setDrawerMode('edit');
    setSelectedDebit(debit);
    reset({
      debit_date: debit?.debit_date ? String(debit.debit_date).slice(0, 10) : '',
      beneficiary: debit?.beneficiary ?? '',
      debit_type: debit?.debit_type ?? '',
      debit_from: debit?.debit_from ?? '',
      inr: debit?.inr ?? '',
      aed: debit?.aed ?? '',
      return_amount: debit?.return_amount ?? '',
      cashier: debit?.cashier ?? '',
      comments: debit?.comments ?? '',
    });
    setShowModal(true);
  };

  const closeDrawer = () => {
    setShowModal(false);
    setDrawerMode('add');
    setSelectedDebit(null);
    reset();
  };

  const requestSort = (key) => {
    setSortConfig((prev) => {
      const nextDirection = prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc';
      return { key, direction: nextDirection };
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this debit?')) return;
    try {
      await api.delete(`/debit/${id}`);
      toast.success('Debit deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete debit');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatINR = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="page-header">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title">Debits</h1>
            <p className="page-subtitle">Manage all debit/expense entries</p>
          </div>
          {isAdmin && (
            <button onClick={openAddDrawer} className="btn btn-primary">
              <Plus className="w-4 h-4" />
              Add Debit
            </button>
          )}
        </div>
      </div>

      <div className="card">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
          <div>
            <label className="label">Start Date</label>
            <input
              type="date"
              value={filters.start_date}
              onChange={(e) => setFilters((p) => ({ ...p, start_date: e.target.value }))}
              className="input"
            />
          </div>
          <div>
            <label className="label">End Date</label>
            <input
              type="date"
              value={filters.end_date}
              onChange={(e) => setFilters((p) => ({ ...p, end_date: e.target.value }))}
              className="input"
            />
          </div>
          <div>
            <label className="label">Debit Type</label>
            <select
              value={filters.debit_type}
              onChange={(e) => setFilters((p) => ({ ...p, debit_type: e.target.value }))}
              className="input"
            >
              <option value="">All</option>
              {(options.debit_types || []).map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Debit From</label>
            <select
              value={filters.debit_from}
              onChange={(e) => setFilters((p) => ({ ...p, debit_from: e.target.value }))}
              className="input"
            >
              <option value="">All</option>
              {(options.debit_from_options || []).map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-4">
          <div className="text-sm text-gray-600">
            Total: <span className="font-medium">{debits.length}</span>
            {' '}| Showing: <span className="font-medium">{filteredDebits.length}</span>
          </div>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => {
                const cleared = { start_date: '', end_date: '', debit_type: '', debit_from: '' };
                setFilters(cleared);
                fetchData(cleared);
              }}
            >
              Clear
            </button>
            <button type="button" className="btn btn-primary" onClick={() => fetchData()}>
              Apply
            </button>
          </div>
        </div>
      </div>

      {/* Debits Table */}
      <div className="card">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div />
          <div className="w-full sm:w-80">
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input"
              placeholder="Search debits..."
            />
          </div>
        </div>
        {filteredDebits.length > 0 ? (
          <div className="table-container overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th className="cursor-pointer select-none" onClick={() => requestSort('debit_date')}>Date</th>
                  <th className="cursor-pointer select-none" onClick={() => requestSort('beneficiary')}>Beneficiary</th>
                  <th className="cursor-pointer select-none" onClick={() => requestSort('debit_type')}>Type</th>
                  <th className="cursor-pointer select-none" onClick={() => requestSort('debit_from')}>From</th>
                  <th className="cursor-pointer select-none" onClick={() => requestSort('inr')}>INR</th>
                  <th className="cursor-pointer select-none" onClick={() => requestSort('aed')}>AED</th>
                  <th className="cursor-pointer select-none" onClick={() => requestSort('sent_money')}>Sent</th>
                  <th className="cursor-pointer select-none" onClick={() => requestSort('cashier')}>Cashier</th>
                  <th className="cursor-pointer select-none" onClick={() => requestSort('created_by')}>User</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredDebits.map((debit) => (
                  <tr key={debit.id}>
                    <td>{formatDate(debit.debit_date)}</td>
                    <td className="font-medium">{debit.beneficiary}</td>
                    <td>
                      <span className="badge badge-info">{debit.debit_type}</span>
                    </td>
                    <td>{debit.debit_from}</td>
                    <td className="font-semibold text-green-700">{formatINR(debit.inr)}</td>
                    <td>{formatCurrency(debit.aed)}</td>
                    <td className="font-semibold text-red-600">{formatCurrency(debit.sent_money)}</td>
                    <td>{debit.cashier}</td>
                    <td>{debit.created_by || '-'}</td>
                    <td>
                      <div className="flex items-center gap-2">
                        {isAdmin && (
                          <button
                            onClick={() => openEditDrawer(debit)}
                            className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                            title="Edit"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                        )}
                        {isAdmin && (
                          <button
                            onClick={() => handleDelete(debit.id)}
                            className="p-1 text-red-600 hover:bg-red-100 rounded"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-center py-8 text-gray-500">No debit entries found</p>
        )}
      </div>

      {/* Add Debit Drawer */}
      {showModal && (
        <>
          <div className="drawer-overlay" onClick={closeDrawer} />
          <div className="drawer-panel">
            <div className="h-full flex flex-col">
              <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
                <h2 className="text-lg font-semibold">{drawerMode === 'edit' ? 'Edit Debit' : 'Add New Debit'}</h2>
                <button onClick={closeDrawer} className="p-2 rounded-lg hover:bg-gray-100">
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-5">
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="label">Date *</label>
                      <input
                        type="date"
                        {...register('debit_date', { required: 'Date is required' })}
                        className={`input ${errors.debit_date ? 'input-error' : ''}`}
                      />
                    </div>
                    <div>
                      <label className="label">Beneficiary *</label>
                      <input
                        type="text"
                        {...register('beneficiary', { required: 'Beneficiary is required' })}
                        className={`input ${errors.beneficiary ? 'input-error' : ''}`}
                        placeholder="Enter beneficiary name"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="label">Debit Type *</label>
                      <select {...register('debit_type', { required: 'Type is required' })} className="input">
                        <option value="">Select type</option>
                        {(options.debit_types || []).map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                        <option value="Medical">Medical</option>
                        <option value="Education">Education</option>
                        <option value="Food">Food/Ration</option>
                        <option value="Housing">Housing</option>
                        <option value="Emergency">Emergency</option>
                        <option value="Administrative">Administrative</option>
                      </select>
                    </div>
                    <div>
                      <label className="label">Debit From *</label>
                      <select {...register('debit_from', { required: 'Required' })} className="input">
                        <option value="">Select source</option>
                        {(options.debit_from_options || []).map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                        <option value="Zakath">Zakath</option>
                        <option value="Sathaqa">Sathaqa</option>
                        <option value="General">General</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="label">INR Amount</label>
                      <input
                        type="number"
                        step="0.01"
                        {...register('inr')}
                        className="input"
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <label className="label">AED Amount</label>
                      <input
                        type="number"
                        step="0.01"
                        {...register('aed')}
                        className="input"
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <label className="label">Return Amount</label>
                      <input
                        type="number"
                        step="0.01"
                        {...register('return_amount')}
                        className="input"
                        placeholder="0.00"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="label">Cashier *</label>
                      <select {...register('cashier', { required: 'Cashier is required' })} className={`input ${errors.cashier ? 'input-error' : ''}`}>
                        <option value="">Select cashier</option>
                        {(options.cashiers || []).map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="label">Comments</label>
                      <textarea
                        {...register('comments')}
                        className="input"
                        rows="2"
                        placeholder="Add comments"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 pt-4">
                    <button type="button" onClick={closeDrawer} className="btn btn-outline">
                      Cancel
                    </button>
                    <button type="submit" disabled={isSubmitting} className="btn btn-primary">
                      {isSubmitting ? 'Saving...' : (drawerMode === 'edit' ? 'Update Debit' : 'Save Debit')}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
